import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { Briefcase, Search, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Select } from "@/components/ui/input";
import { useDict, useLocale, fill } from "@/lib/i18n/context";
import {
  SALARY_BUCKETS,
  type SalaryBucketId,
} from "@/lib/salary-buckets";

type PublicJob = {
  id: string;
  title: string;
  company_name: string;
  location: string | null;
  seniority: string | null;
  employment_type: string | null;
  salary_range: string | null;
  expires_at: string | null;
  created_at: string;
};

type CareersSort =
  | "newest"
  | "oldest"
  | "title_asc"
  | "title_desc"
  | "company_asc"
  | "expires_soon";

type CareersFilters = {
  q: string;
  location: string;
  seniority: string;
  employment_type: string;
  salary_bucket: string;
  sort: CareersSort;
};

const SORT_OPTIONS: CareersSort[] = [
  "newest",
  "oldest",
  "title_asc",
  "title_desc",
  "company_asc",
  "expires_soon",
];

function parseSort(value: string | null): CareersSort {
  if (value && (SORT_OPTIONS as string[]).includes(value)) {
    return value as CareersSort;
  }
  return "newest";
}

const SENIORITY_OPTIONS = [
  "Intern",
  "Junior",
  "Mid",
  "Senior",
  "Lead",
  "Manager",
];

const EMPLOYMENT_OPTIONS = [
  "Full-time",
  "Part-time",
  "Contract",
  "Internship",
];

function filtersFromParams(params: URLSearchParams): CareersFilters {
  return {
    q: params.get("q") || "",
    location: params.get("location") || "",
    seniority: params.get("seniority") || "",
    employment_type: params.get("employment_type") || "",
    salary_bucket: params.get("salary_bucket") || "",
    sort: parseSort(params.get("sort")),
  };
}

function toQueryString(f: CareersFilters): string {
  const params = new URLSearchParams();
  if (f.q.trim()) params.set("q", f.q.trim());
  if (f.location.trim()) params.set("location", f.location.trim());
  if (f.seniority) params.set("seniority", f.seniority);
  if (f.employment_type) params.set("employment_type", f.employment_type);
  if (f.salary_bucket) params.set("salary_bucket", f.salary_bucket);
  if (f.sort && f.sort !== "newest") params.set("sort", f.sort);
  return params.toString();
}

export function CareersPage() {
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<CareersFilters>(() =>
    filtersFromParams(searchParams),
  );
  const [jobs, setJobs] = useState<PublicJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [, startTransition] = useTransition();
  const firstNav = useRef(true);

  const hasFilters = useMemo(
    () =>
      Boolean(
        filters.q.trim() ||
          filters.location.trim() ||
          filters.seniority ||
          filters.employment_type ||
          filters.salary_bucket,
      ),
    [filters],
  );

  useEffect(() => {
    setFilters(filtersFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    if (firstNav.current) {
      firstNav.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const qs = toQueryString(filters);
      startTransition(() =>
        navigate(qs ? `/careers?${qs}` : "/careers", { replace: true }),
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, navigate]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const qs = searchParams.toString();
    fetch(qs ? `/api/careers?${qs}` : "/api/careers")
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json() as Promise<{ jobs: PublicJob[] }>;
      })
      .then((data) => {
        if (active) setJobs(data.jobs);
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [searchParams]);

  function setFilter<K extends keyof CareersFilters>(
    key: K,
    value: CareersFilters[K],
  ) {
    setFilters((s) => ({ ...s, [key]: value }));
  }

  function clearFilters() {
    setFilters({
      q: "",
      location: "",
      seniority: "",
      employment_type: "",
      salary_bucket: "",
      sort: "newest",
    });
  }

  const sortLabels: Record<CareersSort, string> = {
    newest: t.careers.sortNewest,
    oldest: t.careers.sortOldest,
    title_asc: t.careers.sortTitleAsc,
    title_desc: t.careers.sortTitleDesc,
    company_asc: t.careers.sortCompanyAsc,
    expires_soon: t.careers.sortExpiresSoon,
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t.careers.title}</h1>
        <p className="text-muted">{t.careers.subtitle}</p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <Input
              value={filters.q}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder={t.careers.searchPlaceholder}
              className="ps-9"
            />
          </div>
          <Input
            value={filters.location}
            onChange={(e) => setFilter("location", e.target.value)}
            placeholder={t.careers.locationPlaceholder}
            className="sm:w-44"
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select
            value={filters.seniority}
            onChange={(e) => setFilter("seniority", e.target.value)}
            className="sm:w-40"
          >
            <option value="">{t.careers.allSeniority}</option>
            {SENIORITY_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
          <Select
            value={filters.employment_type}
            onChange={(e) => setFilter("employment_type", e.target.value)}
            className="sm:w-44"
          >
            <option value="">{t.careers.allEmployment}</option>
            {EMPLOYMENT_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
          <Select
            value={filters.salary_bucket}
            onChange={(e) => setFilter("salary_bucket", e.target.value)}
            className="sm:w-48"
          >
            <option value="">{t.careers.allSalary}</option>
            {SALARY_BUCKETS.map((b) => (
              <option key={b.id} value={b.id}>
                {t.careers.salaryBuckets[b.id as SalaryBucketId]}
              </option>
            ))}
          </Select>
          <Select
            value={filters.sort}
            onChange={(e) =>
              setFilter("sort", parseSort(e.target.value))
            }
            className="sm:w-48"
            aria-label={t.careers.sortBy}
          >
            {SORT_OPTIONS.map((id) => (
              <option key={id} value={id}>
                {sortLabels[id]}
              </option>
            ))}
          </Select>
          {hasFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="sm:ms-auto"
            >
              <X className="h-4 w-4" /> {t.careers.clearFilters}
            </Button>
          )}
        </div>
      </div>

      {loading && !jobs ? (
        <div className="flex min-h-[20vh] items-center justify-center text-muted">
          Loading…
        </div>
      ) : error && !jobs ? (
        <div className="flex min-h-[20vh] items-center justify-center text-muted">
          {error}
        </div>
      ) : !jobs || jobs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <Briefcase className="h-10 w-10 text-muted/60" />
            <p className="mt-4 font-medium">
              {hasFilters ? t.careers.noMatches : t.careers.empty}
            </p>
            <p className="text-sm text-muted">
              {hasFilters ? t.careers.noMatchesDesc : t.careers.emptyDesc}
            </p>
            {hasFilters && (
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={clearFilters}
              >
                {t.careers.clearFilters}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <ul
          className={`divide-y divide-border overflow-hidden rounded-xl border border-border bg-card ${
            loading ? "opacity-60" : ""
          }`}
        >
          {jobs.map((job) => (
            <li key={job.id}>
              <Link
                to={`/careers/${job.id}`}
                className="block px-4 py-4 transition hover:bg-surface"
              >
                <div className="font-semibold text-foreground">{job.title}</div>
                <div className="mt-1 text-sm text-muted">
                  {job.company_name}
                  {" · "}
                  {job.location || t.careers.remote}
                  {(job.seniority || job.employment_type || job.salary_range) && (
                    <>
                      {" · "}
                      {[job.seniority, job.employment_type, job.salary_range]
                        .filter(Boolean)
                        .join(" · ")}
                    </>
                  )}
                </div>
                {job.expires_at && (
                  <div className="mt-1 text-xs text-muted">
                    {fill(t.careers.expires, {
                      date: format(new Date(job.expires_at), "PP", {
                        locale: dateLocale,
                      }),
                    })}
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
